import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SlowMotionVideoOutlinedIcon from '@mui/icons-material/SlowMotionVideoOutlined';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import React from 'react';
import { AvatarMedium, NamedLink } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { userDisplayNameAsString } from '../../../util/data';
import { getProfilePageId } from '../../../util/profileHandle';
import { formatDateRangeWithProximity, formatDuration } from '../../../util/dates';
import {
  getModeLabel,
  isListingOnSchoolBehalf,
  LISTING_FIELD_MODE,
} from '../../../util/soulwise-marketplace/listingFields';
import {
  isDigitalProductListingType,
  isIndividualSession,
} from '../../../util/soulwise-marketplace/listingType';
import { capitalize } from '../../../util/string';
import { formatDigitalContentSummary } from '../../../util/digitalMedia';
import { FormattedMessage } from '../../../util/reactIntl';
import { getLanguageOptionKey } from '../../../util/listingFieldI18n';
import css from './ListingDetails.module.css';

const isValidDateValue = value => {
  if (value == null || value === '') {
    return false;
  }
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime());
};

export const ListingDateRange = ({
  className,
  startDate,
  endDate,
  intl,
  fetchInProgress,
  hasTimeslots,
}) => {
  const classes = classNames(css.ListingDateRange, className);
  const hasDates = isValidDateValue(startDate) && isValidDateValue(endDate);

  if (fetchInProgress) {
    return (
      <div className={classes}>
        <Skeleton variant="rectangular" width={120} height={34} />
      </div>
    );
  }

  if (!hasTimeslots) {
    return (
      <div className={classes}>
        <FormattedMessage id="ListingDetails.noMoreDates" />
      </div>
    );
  }

  if (!hasDates) {
    return (
      <div className={classes}>
        <Skeleton variant="rectangular" width={120} height={34} />
      </div>
    );
  }

  const {
    date,
    timeRange,
    isSameDay,
    endDateFormated,
    startDateFormated,
  } = formatDateRangeWithProximity(new Date(startDate), new Date(endDate), intl, {});

  return (
    <div className={classes}>
      {isSameDay && (
        <>
          <p>{date}</p>
          <p>{timeRange}</p>
        </>
      )}
      {!isSameDay && (
        <>
          <p>{startDateFormated}</p>
          <p>{endDateFormated}</p>
        </>
      )}
    </div>
  );
};

/**
 * @component
 * @param {Object} props - The component props.
 * @param {propTypes.user} props.author - The author of the listing.
 * @param {propTypes.listing} props.listing - The listing details.
 * @param {Object} props.intl - The internationalization object for formatting.
 * @param {Date} props.timeslotStart - The start date of the timeslot.
 * @param {Date} props.timeslotEnd - The end date of the timeslot.
 * @param {boolean} props.fetchTimeSlotsInProgress - Indicates if fetching timeslots is in progress.
 */
export const ListingDetails = props => {
  const {
    author,
    listing,
    intl,
    timeslotStart,
    timeslotEnd,
    fetchTimeSlotsInProgress,
    fetchFirstTimeslotInProgress,
    hasTimeslots,
  } = props;
  const { attributes } = listing;
  const config = useConfiguration();
  const authorDisplayName = userDisplayNameAsString(author, '');
  const { location } = attributes?.publicData ?? {};
  const duration = attributes?.publicData?.duration;

  const isOnline = attributes?.publicData?.mode === LISTING_FIELD_MODE.online;
  const isIndividual = isIndividualSession(attributes?.publicData?.listingType);
  const isDigital = isDigitalProductListingType(attributes?.publicData?.listingType);
  const onlineModeLabel = getModeLabel(attributes?.publicData?.mode, config, intl);
  const languages = attributes?.publicData?.language?.length
    ? attributes.publicData.language
        .map(lang =>
          intl.formatMessage({
            id: `Language.${getLanguageOptionKey(lang)}`,
            defaultMessage: capitalize(lang),
          })
        )
        .join(', ')
    : '';
  const languageLabel = languages
    ? intl.formatMessage({ id: 'ListingDetails.language' }, { languages })
    : '';
  const linkProps = author.id
    ? { name: 'ProfilePage', params: { id: getProfilePageId(author) } }
    : { name: 'ProfileBasePage' };

  const isSchool = isListingOnSchoolBehalf(listing);
  const authorLabel = isSchool
    ? intl.formatMessage({ id: 'ListingDetails.school' })
    : intl.formatMessage({ id: 'ListingDetails.practitioner' });
  const datesFetchInProgress = fetchTimeSlotsInProgress && !timeslotStart && !timeslotEnd;
  const authorReviewStats = author?.attributes?.profile?.publicData?.reviewStats;

  if (isDigital) {
    const digitalMedia = listing?.attributes?.publicData?.digitalMedia;
    const digitalItems = digitalMedia?.items || [];
    const contentSummary = formatDigitalContentSummary(digitalItems, intl);
    return (
      <div className={css.ListingDetails_DigitalWrap}>
        <div className={`${css.ListingDetails_Container} ${css.ListingDetails_ContainerDigital}`}>
          {contentSummary ? (
            <div className={css.listingDetails_Quadrant}>
              <div className={css.ListingDetails_Wrapper}>
                <div className={css.ListingDetails_Icon}>
                  <AttachFileOutlinedIcon />
                </div>
                <div className={css.ListingDetails_Text}>
                  <p>
                    <FormattedMessage
                      id="ListingPage.digitalContentSummary"
                      values={{ summary: contentSummary }}
                    />
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          <div className={`${css.listingDetails_Quadrant} ${css.lastOnMobile}`}>
            <NamedLink
              {...linkProps}
              className={`${css.ListingDetails_Wrapper} ${css.ListingDetails_Author}`}
            >
              <div className={css.ListingDetails_Author_Avatar}>
                <AvatarMedium user={author} disableProfileLink={true} />
              </div>
              <div className={css.ListingDetails_Text}>
                <p className={css.practitionerLabel}>{authorLabel}</p>
                <p className={css.ListingDetails_AuthorRow}>
                  <span className={css.ListingDetails_AuthorName}>{authorDisplayName}</span>
                  {authorReviewStats?.averageRating && (
                    <span className={css.ListingDetails_Rating}>
                      ★ {authorReviewStats.averageRating.toFixed(1)}
                    </span>
                  )}
                </p>
              </div>
            </NamedLink>
          </div>
          <div className={css.listingDetails_Quadrant}>
            <div className={css.ListingDetails_Wrapper}>
              <div className={css.ListingDetails_Icon}>
                <ChatBubbleOutlineOutlinedIcon />
              </div>
              <div className={css.ListingDetails_Text}>
                <p
                  className={`${css.ListingDetails_Text_TruncateMultiLine} ${css.cursoDefault}`}
                  title={languageLabel}
                >
                  {languageLabel}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={css.ListingDetails_Container}>
      <div className={css.listingDetails_Quadrant}>
        <div className={css.ListingDetails_Wrapper}>
          <div className={css.ListingDetails_Icon}>
            {isIndividual ? <ScheduleIcon /> : <CalendarMonthOutlinedIcon />}
          </div>
          {isIndividual ? (
            <span className={css.ListingDetails_Text}>
              {formatDuration({ minutes: duration, intl })}
            </span>
          ) : (
            <ListingDateRange
              className={css.ListingDetails_Text}
              startDate={timeslotStart}
              endDate={timeslotEnd}
              hasTimeslots={hasTimeslots}
              intl={intl}
              fetchInProgress={datesFetchInProgress || fetchFirstTimeslotInProgress}
            />
          )}
        </div>
      </div>
      <div className={css.listingDetails_Quadrant}>
        <div className={css.ListingDetails_Wrapper}>
          {!isOnline && (
            <>
              <div className={css.ListingDetails_Icon}>
                <FmdGoodOutlinedIcon />
              </div>
              <div className={css.ListingDetails_Text}>
                <p className={css.ListingDetails_Text_Truncate}>
                  {location?.venueName ||
                    location?.name ||
                    intl.formatMessage({ id: 'ListingDetails.noLocation' })}
                </p>
              </div>
            </>
          )}
          {isOnline && (
            <>
              <div className={css.ListingDetails_Icon}>
                <SlowMotionVideoOutlinedIcon />
              </div>
              <div className={css.ListingDetails_Text}>
                <p className={css.ListingDetails_Text_Truncate}>{onlineModeLabel}</p>
              </div>
            </>
          )}
        </div>
      </div>
      <div className={`${css.listingDetails_Quadrant} ${css.lastOnMobile}`}>
        <NamedLink
          {...linkProps}
          className={`${css.ListingDetails_Wrapper} ${css.ListingDetails_Author}`}
        >
          <div className={css.ListingDetails_Author_Avatar}>
            <AvatarMedium user={author} disableProfileLink={true} />
          </div>
          <div className={css.ListingDetails_Text}>
            <p className={css.practitionerLabel}>{authorLabel}</p>
            <p className={css.ListingDetails_AuthorRow}>
              <span className={css.ListingDetails_AuthorName}>{authorDisplayName}</span>
              {authorReviewStats?.averageRating && (
                <span className={css.ListingDetails_Rating}>
                  ★ {authorReviewStats.averageRating.toFixed(1)}
                </span>
              )}
            </p>
          </div>
        </NamedLink>
      </div>
      <div className={css.listingDetails_Quadrant}>
        <div className={css.ListingDetails_Wrapper}>
          <div className={css.ListingDetails_Icon}>
            <ChatBubbleOutlineOutlinedIcon />
          </div>
          <div className={css.ListingDetails_Text}>
            <p
              className={`${css.ListingDetails_Text_TruncateMultiLine} ${css.cursoDefault}`}
              title={languageLabel}
            >
              {languageLabel}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
